import fetchBuilder from "fetch-retry-ts";

export interface ApiConfig {
    baseURL?: string;
    useEnv?: boolean;
    apiPrefix: string;
    timeout?: number;
}

type RequestMethod = "GET" | "PATCH" | "DELETE" | "POST" | "PUT";

type RequestParams = {
    method: RequestMethod;
    body?: string;
    headers: {
        [name: string]: string;
    };
};

export class ApiClient {
    private readonly baseUrl: string;

    constructor(config: ApiConfig) {
        if (!config.baseURL && !config.useEnv) {
            throw new Error("baseUrl must be provided");
        }

        const apiBaseUrl = config.useEnv
            ? process.env.NEXT_PUBLIC_BASE_AWS_API_URL
            : config.baseURL;

        this.baseUrl = `${apiBaseUrl}/${config.apiPrefix}`;
    }

    private async request<T>(url: string, method: RequestMethod = "GET", body?: any): Promise<T> {
        const params: RequestParams = {
            method,
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        };

        if (body) {
            params.body = JSON.stringify(body);
        }

        const options = {
            retries: 3,
            retryDelay: (attempt: number, error: Error | null, response: Response | null): number =>
                Math.pow(2, attempt) * 200,
            retryOn: [429, 500, 502, 503, 504],
        };

        const newFetch = fetchBuilder(fetch, options);

        const response = await newFetch(`${this.baseUrl}${url}`, params);

        if (response.status === 204) {
            return {} as T;
        }

        const responseJson = await response.json();
        return responseJson as T;
    }

    async makeGetRequest<T>(url: string): Promise<T> {
        const response = await this.request<T>(url, "GET");
        return response;
    }

    async makePostRequest<T>(url: string, data?: any): Promise<T> {
        const response = await this.request<T>(url, "POST", data);
        return response;
    }

    async makePutRequest<T>(url: string, data: any): Promise<T> {
        const response = await this.request<T>(url, "PUT", data);
        return response;
    }

    async makeDeleteRequest<T>(url: string): Promise<T> {
        const response = await this.request<T>(url, "DELETE");
        return response;
    }

    async makePatchRequest<T>(url: string, data: any): Promise<T> {
        const response = await this.request<T>(url, "PATCH", data);
        return response;
    }
}
